import { useMemo, useRef, type RefObject } from 'react';

import { useAuth } from '@verantos-dev/core-auth';
import { getGatewayApiBase, getGatewayAuthBase } from '@verantos-dev/core-constants';

import { useGatewayChat } from './useGatewayChat';
import { type GatewayChatStreamConfig } from './useGatewayChatStream';
import { type GatewayConversationManagerConfig } from './useGatewayConversationManager';
import { type GatewayPromptStateConfig } from './useGatewayPromptState';
import { type UseGatewaySessionAuthConfig } from './useGatewaySessionAuth';

export interface UseChatWindowBaseGatewayConfig {
  session?: UseGatewaySessionAuthConfig;
  stream?: GatewayChatStreamConfig;
  conversation?: GatewayConversationManagerConfig;
  prompt?: GatewayPromptStateConfig;
}

export interface UseChatWindowBaseOptions {
  apiBase?: string; // Optional: Override gateway API base (uses centralized default)
  authBase?: string; // Optional: Override auth base (uses centralized default)
  gateway?: UseChatWindowBaseGatewayConfig;
  isOwner?: boolean;
}

export interface UseChatWindowBaseResult {
  userInitials: string;
  isOwner: boolean;
  chatWindowRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
  session: GatewayChatResult['session'];
  stream: GatewayChatResult['stream'];
  conversations: GatewayChatResult['conversations'];
  prompt: GatewayChatResult['prompt'];
  isReady: boolean;
  isWaitingForResponse: boolean;
}

type GatewayChatResult = ReturnType<typeof useGatewayChat>;

const resolveGatewayBases = (options?: UseChatWindowBaseOptions) => {
  const apiBase = options?.apiBase ?? getGatewayApiBase();
  const authBase = options?.authBase ?? import.meta.env.VITE_AUTH_BASE ?? getGatewayAuthBase();
  return { apiBase, authBase };
};

export const useChatWindowBase = (options?: UseChatWindowBaseOptions): UseChatWindowBaseResult => {
  const {
    user: { userInitials },
  } = useAuth();

  const chatWindowRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { apiBase, authBase } = resolveGatewayBases(options);

  const gateway = useGatewayChat({
    apiBase,
    authBase,
    sessionConfig: options?.gateway?.session,
    streamConfig: options?.gateway?.stream,
    conversationConfig: options?.gateway?.conversation,
    promptConfig: options?.gateway?.prompt,
  });

  const { session, stream, conversations, prompt } = gateway;

  const isReady = useMemo(
    () =>
      session.sessionReady &&
      // Allow input if no conversation is selected (user can type first message)
      // OR if connected to a conversation
      (!conversations.activeConversationId || stream.connectionStatus === 'connected'),
    [conversations.activeConversationId, session.sessionReady, stream.connectionStatus]
  );

  const isWaitingForResponse = useMemo(
    () =>
      stream.connectionStatus === 'connected' &&
      (Boolean(stream.currentRunId) || stream.progressState.active || prompt.isSending),
    [prompt.isSending, stream.connectionStatus, stream.currentRunId, stream.progressState.active]
  );

  const isOwner = options?.isOwner ?? false;

  return {
    userInitials,
    isOwner,
    chatWindowRef,
    messagesEndRef,
    session,
    stream,
    conversations,
    prompt,
    isReady,
    isWaitingForResponse,
  };
};
